import * as React from 'react';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { Tabs } from '@cian/ui-kit/prev';
import { useSelector } from 'react-redux';

import { getInitialTab, getSectionTabs, getSectionTitle } from './helpers';
import { trackMortgageOffersTabClick, trackPromoOffersShow } from './tracking';
import { Layout } from '../../components/Layout';
import { MortgageSection } from '../../components/MortgageSection';
import { useIsMortgageBrokerBannerAvailable } from '../../hooks/useIsMortgageBrokerBannerAvailable';
import { getHasBanks, getNewbuildingId } from '../../selectors/newbuilding';
import { selectHasMortgagePromos } from '../../selectors/newbuildingMorgageCalculator';
import { EMortgageOffersTabs } from '../../types/mortgageOffers';
import { IPromo } from '../../types/newbuilding/promos';
import { useApplicationContext } from '../../utils/withApplicationContext';
import { AccreditedBanksContainer } from '../AccreditedBanks';
import { MortgageOffersContainer } from '../MortgageOffers';

export const MortgageSectionContainer: React.FC = () => {
  const { config } = useApplicationContext();
  const hasMortgageOffers = useSelector(selectHasMortgagePromos);
  const hasBanks = useSelector(getHasBanks);
  const newbuildingId = useSelector(getNewbuildingId);
  const isMortgageBrokerBannerAvailable = useIsMortgageBrokerBannerAvailable();
  const mortgageMiniCalculatorEnabled = config.get<boolean>('Newbuilding.MortgageMiniCalculator.Enabled');

  const { tabs, title } = React.useMemo(
    () => ({
      tabs: getSectionTabs({ hasBanks, hasMortgageOffers }),
      title: getSectionTitle({ hasBanks, hasMortgageOffers }),
    }),
    [hasBanks, hasMortgageOffers],
  );

  const [activeTab, setActiveTab] = React.useState(getInitialTab(tabs));

  const displayedOffersRef = React.useRef<IPromo[]>([]);

  const handleDisplayedOffersChange = React.useCallback((displayedOffers: IPromo[]) => {
    displayedOffersRef.current = displayedOffers;
  }, []);

  const handleShow = React.useCallback(() => {
    trackPromoOffersShow(displayedOffersRef.current, newbuildingId, true);
  }, [newbuildingId]);

  const handleTabChange = React.useCallback((tab: EMortgageOffersTabs) => {
    setActiveTab(tab);
    trackMortgageOffersTabClick(tab);
  }, []);

  const hasTabs = tabs.length > 0;
  const isTabsNodeVisible = tabs.length > 1;

  if (!hasTabs || isMortgageBrokerBannerAvailable || mortgageMiniCalculatorEnabled) {
    return null;
  }

  return (
    <Layout>
      <ActionAfterViewObserver callback={handleShow} triggerOnce>
        <MortgageSection
          title={title}
          tabs={isTabsNodeVisible && <Tabs activeTab={activeTab} onChange={handleTabChange} tabs={tabs} />}
        >
          {activeTab === EMortgageOffersTabs.Mortgage && (
            <MortgageOffersContainer onDisplayedOffersChange={handleDisplayedOffersChange} />
          )}
          {activeTab === EMortgageOffersTabs.Banks && <AccreditedBanksContainer />}
        </MortgageSection>
      </ActionAfterViewObserver>
    </Layout>
  );
};
